import React, { useRef, useState, useMemo, useCallback } from "react";
import { EditOutlined, DeleteOutlined, SwapOutlined, FileImageOutlined } from "@ant-design/icons";
import { Card, Button, Modal, Row, Col, message } from "antd";
import { connect } from "dva";
import { ShipmentStatus, QuoteStatus, DealerExchangeType } from "@/utils/enum";
import { getUrlParams, showJiraWidget, hideJiraWidget, getGuidFirst8 } from "@/utils/utils";
import { getBOL } from "@/services/vehicles";
import BulkPriceAdjustmentModal from "@/components/BulkPriceAdjustmentModal";
import MoreMenu from "@/components/MoreMenu";
import VehiclesList from "@/components/VehiclesList";
import CreateQuoteMenuItem from "@/pages/AdminShipments/AdminShipmentView/CreateQuoteMenuItem";
import EditVehicleMenuItem from "@/pages/AdminShipments/AdminShipmentView/EditVehicleMenuItem";
import ScanStatusList from "@/pages/AdminShipments/AdminShipmentView/ScanStatusList/ScanStatusList";
import VehicleImagesModal from "@/pages/AdminShipments/AdminShipmentView/VehicleImagesModal";
import EditQuoteDrawer from "@/pages/AdminShipments/AdminShipmentView/components/EditQuoteDrawer";
import ShipmentActions from "@/pages/AdminShipments/AdminShipmentView/components/ShipmentActions";
import AddVehicle from "@/pages/ShipmentList/ShipmentDetails/components/VehicleListContainer/AddVehicle";
import QuoteVehicle from "@/pages/ShipmentList/ShipmentDetails/components/VehicleListContainer/QuoteVehicle";
import TransferVehicleModal from "./TransferVehicleModal";

const { confirm } = Modal;

const AdminVehiclesList = ({
  dispatch,
  adminShipmentView,
  configPerm,
  exchangeShipmentStatus,
  exchangeVehiclesAllQuoted,
  submitting,
  onNotesUpdate,
  toggleDispatchModal,
}) => {
  const postShipmentFormRef = useRef(null);
  const shipmentUserForm = useRef(null);

  const [isLoadingBOL, setIsLoadingBOL] = useState(false);
  const [selectedVehicleImages, setSelectedVehicleImages] = useState(null);
  const [selectedVehicleTitle, setSelectedVehicleTitle] = useState("");
  const [selectShipmentUserVisible, setSelectShipmentUserVisible] = useState(false);

  const [selectedVehicleData, setSelectedVehicleData] = useState(null);
  const [transferVehicleId, setTransferVehicleId] = useState(null);
  const [visibleCreateQuote, setVisibleCreateQuote] = useState(false);
  const [visibleEditVehicle, setVisibleEditVehicle] = useState(false);
  const [editingQuoteVehicle, setEditingQuoteVehicle] = useState(null);

  const adminShipmentData = useMemo(() => adminShipmentView?.data, [adminShipmentView]);

  const postShipmentButtonDisabled = useMemo(() => {
    const noVehicles = adminShipmentData.vehicles.length === 0;

    const aVehicleHasntCompletedQuote =
      adminShipmentData.vehicles.filter(
        vehicle =>
          (vehicle?.quote?.status !== QuoteStatus.Completed &&
            adminShipmentView.quotesUpdated[vehicle?.id]?.status !== QuoteStatus.Completed) ||
          vehicle?.quote?.expired,
      ).length > 0;

    const hasOtherLeg = adminShipmentData.childShipmentId || adminShipmentData.parentShipmentId;

    const otherLegNotReady =
      hasOtherLeg && (exchangeShipmentStatus !== ShipmentStatus.Completed || !exchangeVehiclesAllQuoted);

    return noVehicles || aVehicleHasntCompletedQuote || otherLegNotReady;
  }, [adminShipmentView, exchangeShipmentStatus, exchangeVehiclesAllQuoted, adminShipmentData]);

  const selectVehicleImages = useCallback(vehicle => {
    setIsLoadingBOL(true);
    getBOL(vehicle.id)
      .then(response => {
        setSelectedVehicleImages(response);
        setSelectedVehicleTitle(`${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`);
        setIsLoadingBOL(false);
      })
      .catch(err => {
        console.error(err);
        setIsLoadingBOL(false);
        message.error("Error fetching BOL images");
      });
  }, []);

  const closeVehicleImagesModal = useCallback(() => {
    setSelectedVehicleImages(null);
    setSelectedVehicleTitle("");
  }, []);

  const cancelUserModal = useCallback(() => {
    setSelectShipmentUserVisible(false);
  }, []);

  const hideQuoteCreateDrawer = useCallback(() => {
    setVisibleCreateQuote(false);
    showJiraWidget();
  }, []);

  const openUserModal = useCallback(
    confirmedDoublePostExchange => {
      if ((adminShipmentData.childShipmentId || adminShipmentData.parentShipmentId) && !confirmedDoublePostExchange) {
        confirm({
          title: "Warning",
          content:
            "This shipment is part of a dealer exchange. If this shipment is posted, the other shipment in the exchange will also be posted. Are you sure?",
          onOk() {
            openUserModal(true);
          },
        });

        return;
      }

      setSelectShipmentUserVisible(true);
    },
    [adminShipmentData],
  );

  const confirmShipment = useCallback(() => {
    const shipmentId = getUrlParams("shipmentId");

    const shipmentUserFormPromise = shipmentUserForm?.current?.validateFields();
    const postShipmentFormRefPromise = postShipmentFormRef?.current?.validateFields();

    Promise.all([shipmentUserFormPromise, postShipmentFormRefPromise]).then(() => {
      dispatch({
        type: "adminShipmentView/postAdminShipment",
        shipmentId,
      }).then(res => {
        if (!res.error) {
          setSelectShipmentUserVisible(false);
        }
      });
    });
  }, [dispatch]);

  const refreshData = useCallback(() => {
    dispatch({
      type: "adminShipmentView/fetchSingleAdminShipment",
      shipmentId: adminShipmentData.id,
    });
  }, [dispatch, adminShipmentData]);

  const extra = useMemo(() => {
    const shipmentId = adminShipmentData?.id;
    const aVehicleNeedsQuote =
      adminShipmentData?.vehicles?.filter(vehicle => vehicle?.quote?.status === QuoteStatus.None).length > 0;
    const canQuote = aVehicleNeedsQuote && adminShipmentData.status <= ShipmentStatus.Pending;

    const quoteValidation = canQuote && configPerm["CoreService.AdminShipments.Vehicles.Request"];

    const vehicles = (adminShipmentData?.vehicles || [])
      .filter(
        vehicle =>
          vehicle?.quote?.status === QuoteStatus.Completed ||
          adminShipmentView?.quotesUpdated?.[vehicle.id]?.status === QuoteStatus.Completed,
      )
      .map(vehicle => ({
        ...vehicle,
        quote: adminShipmentView?.quotesUpdated?.[vehicle.id]
          ? adminShipmentView?.quotesUpdated[vehicle.id]
          : vehicle.quote,
      }));

    return (
      <Row gutter={10} style={{ marginBottom: "24px" }}>
        {adminShipmentData.status >= ShipmentStatus.Assigned && (
          <Col flex={1}>
            <ScanStatusList />
          </Col>
        )}
        {quoteValidation && <QuoteVehicle isAdmin key="quote-vehicles" />}
        <Col flex={1}>
          {adminShipmentData.status < ShipmentStatus.Posted && <AddVehicle refreshData={refreshData} isAdmin />}
        </Col>
        <Col flex={1}>
          <BulkPriceAdjustmentModal
            vehicles={vehicles}
            shipmentId={shipmentId}
            refreshPageData={() => {
              dispatch({
                type: "adminShipmentView/fetchSingleAdminShipment",
                shipmentId,
              });
            }}
          />
        </Col>
      </Row>
    );
  }, [adminShipmentData, adminShipmentView?.quotesUpdated, dispatch, configPerm, refreshData]);

  const showQuoteCreateDrawer = useCallback(() => {
    setVisibleCreateQuote(true);
    hideJiraWidget();
  }, []);

  const handleAdminDeleteVehicleFromShipment = useCallback(
    (vehicleId, shipmentId) => {
      confirm({
        title: "Delete Vehicle",
        content: "Are you sure you want to delete this vehicle?",
        onOk() {
          const hide = message.loading("Deleting vehicle...");
          dispatch({
            type: "adminShipmentView/deleteSingleVehicle",
            vehicleId,
            shipmentId,
          }).then(res => {
            if (!res) {
              message.error("Could not delete vehicle.");
            } else {
              message.success("Successfully deleted vehicle.");

              if (typeof onNotesUpdate === "function") {
                onNotesUpdate();
              }
            }
            hide();
          });
        },
      });
    },
    [dispatch, onNotesUpdate],
  );

  const showVehicleEditDrawer = useCallback(() => {
    setVisibleEditVehicle(true);
    hideJiraWidget();
  }, []);

  const hideVehicleEditDrawer = useCallback(() => {
    setVisibleEditVehicle(false);
    showJiraWidget();
  }, []);

  const onQuoteUpdate = useCallback(
    (quote, vehicle) => {
      dispatch({
        type: "adminShipmentView/updateQuote",
        vehicleId: vehicle.id,
        quote,
      });
    },
    [dispatch],
  );

  const onMenuItemClick = useCallback(
    (key, item) => {
      switch (key) {
        case "edit":
          setSelectedVehicleData(item);
          showVehicleEditDrawer();
          hideJiraWidget();
          break;

        case "delete":
          handleAdminDeleteVehicleFromShipment(item.id, item.shipmentId, item.vehicleId);
          break;

        case "transfer":
          setTransferVehicleId(item.id);
          break;

        case "create-quote":
          setSelectedVehicleData(item);
          showQuoteCreateDrawer();
          hideJiraWidget();
          break;

        case "edit-quote":
          setEditingQuoteVehicle(item);
          hideJiraWidget();
          break;

        default:
          break;
      }
    },
    [handleAdminDeleteVehicleFromShipment, showVehicleEditDrawer, showQuoteCreateDrawer],
  );

  // if we have an ordered exchange and the first leg is undelivered we can't assign this shipment yet
  const isOrderedExchangeUndelivered = useMemo(
    () =>
      adminShipmentData?.parentShipmentId &&
      adminShipmentData?.parentShipmentStatus < ShipmentStatus.Delivered &&
      adminShipmentData?.dealerExchangeType === DealerExchangeType.Ordered,
    [adminShipmentData],
  );

  const renderItemExtra = useCallback(
    item => {
      const quoteCompleted =
        item?.quote?.status === QuoteStatus.Completed ||
        adminShipmentView?.quotesUpdated[item.id]?.status === QuoteStatus.Completed;

      const canChangeQuote = configPerm["CoreService.AdminShipments.Vehicles.EditQuote"] && quoteCompleted;

      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          width: "100%",
        }}
      >
        {adminShipmentData.status <= ShipmentStatus.Assigned && (
          <MoreMenu
            onItemClick={key => onMenuItemClick(key, item)}
            items={[
              {
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
                authority: "CoreService.AdminShipments.Vehicles.Edit",
              },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: "Delete",
                authority: "CoreService.AdminShipments.Vehicles.Delete",
              },
              {
                key: "transfer",
                icon: <SwapOutlined />,
                label: "Transfers",
                authority: "CoreService.AdminShipments.Vehicles.Transfer",
              },
              {
                key: "edit-quote",
                icon: <EditOutlined />,
                label: "Change quote",
                authority: canChangeQuote,
              },
            ]}
          />
        )}
        <Button
          onClick={() => selectVehicleImages(item)}
          icon={<FileImageOutlined />}
          style={{
            marginRight: adminShipmentData.status < ShipmentStatus.Assigned ? "8px" : 0,
          }}
        >
          View Images
        </Button>
      </div>;
    },
    [adminShipmentView?.quotesUpdated, configPerm, adminShipmentData, onMenuItemClick, selectVehicleImages],
  );

  if (!adminShipmentData) {
    return null;
  }

  return (
    <Card>
      <VehiclesList
        multiPrice
        pricesSpacing={2}
        compact={adminShipmentData.status < ShipmentStatus.Assigned}
        vehicles={adminShipmentData?.vehicles || []}
        descriptionColumn={{ md: 1, lg: 2, xxl: 3 }}
        shipmentData={adminShipmentData}
        extra={extra}
        onQuoteUpdate={onQuoteUpdate}
        multiPriceTitles={["Dealer price", "Transporter price"]}
        updatedQuotes={adminShipmentView.quotesUpdated}
        onNotesUpdate={onNotesUpdate}
        priceExtractor={item => [
          adminShipmentView?.quotesUpdated?.[item.id]?.dealerPrice ||
            item?.quote?.dealerPrice ||
            item?.pricingEngineResult?.dealerPrice,
          adminShipmentView?.quotesUpdated?.[item.id]?.transporterPrice ||
            item?.quote?.transporterPrice ||
            item?.pricingEngineResult?.transporterPrice,
        ]}
        itemExtra={renderItemExtra}
      />

      <ShipmentActions
        shipment={adminShipmentData}
        formRef={postShipmentFormRef}
        configPerm={configPerm}
        openUserModal={openUserModal}
        postShipmentButtonDisabled={postShipmentButtonDisabled}
        toggleDispatchModal={toggleDispatchModal}
        assignShipmentButtonDisabled={isOrderedExchangeUndelivered}
      />

      <VehicleImagesModal
        isOpen={!!selectedVehicleImages}
        title={selectedVehicleTitle}
        images={selectedVehicleImages}
        isLoading={isLoadingBOL}
        submitCallback={closeVehicleImagesModal}
        cancelCallback={closeVehicleImagesModal}
      />

      <Modal
        title="Post Shipment"
        visible={selectShipmentUserVisible}
        onOk={confirmShipment}
        onCancel={cancelUserModal}
        confirmLoading={submitting}
      >
        <p>
          Please confirm your intent to post this shipment: <strong>{getGuidFirst8(adminShipmentData?.id)}</strong>
        </p>
      </Modal>

      <TransferVehicleModal transferVehicleId={transferVehicleId} onClose={() => setTransferVehicleId(null)} />

      <EditQuoteDrawer
        visible={editingQuoteVehicle !== null}
        onClose={() => {
          setEditingQuoteVehicle(null);
          showJiraWidget();
        }}
        quoteData={adminShipmentView.quotesUpdated[editingQuoteVehicle?.id] || editingQuoteVehicle?.quote || null}
        onNotesUpdate={onNotesUpdate}
        onQuoteUpdate={newQuoteData => {
          onQuoteUpdate(newQuoteData, editingQuoteVehicle);
        }}
        shipmentId={adminShipmentData.id}
      />

      <EditVehicleMenuItem
        hideVehicleEditDrawer={hideVehicleEditDrawer}
        visible={visibleEditVehicle}
        vehicleData={selectedVehicleData}
        isInternalShipment={adminShipmentData.isInternalShipment}
      />

      <CreateQuoteMenuItem
        hideQuoteCreateDrawer={hideQuoteCreateDrawer}
        visible={visibleCreateQuote}
        quoteId={selectedVehicleData?.quote?.quoteId}
        shipmentData={adminShipmentData}
        vehicleInfo={selectedVehicleData}
        vehicleId={selectedVehicleData?.vehicleId}
      />
    </Card>
  );
};

export default connect(({ adminShipmentView, configs, loading }) => ({
  adminShipmentView,
  configPerm: configs.configPerm,
  submitting: loading.effects["adminShipmentView/postAdminShipment"],
}))(AdminVehiclesList);
